import React, { useState, useEffect, useContext } from "react";
import { TextField, CircularProgress, Box, Typography } from "@mui/material";
import ApexCharts from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { AuthContext } from "../../context/AuthContext";

interface PaymentData {
  status: string;
  total_amount: number;
}

const MonthlyPayments: React.FC = () => {
  const { token } = useContext(AuthContext) || {};

  const [month, setMonth] = useState<string>(
    new Date().toISOString().slice(0, 7)
  );
  const [paymentsData, setPaymentsData] = useState<PaymentData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMonth(event.target.value);
  };

  const fetchData = async () => {
    if (!month) {
      console.warn("Por favor selecciona un mes.");
      return;
    }

    setLoading(true);
    setError(null);

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    fetch(`api/invoices/monthlypayments?month=${month}`, options)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Error al obtener los datos de la API");
        }

        return response.json();
      })
      .then((data) => {
        setPaymentsData(data.data);
      })
      .catch((error) => {
        setError((error as Error).message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, token]);

  // Preparar los datos para la gráfica de barras
  const chartData = {
    series: [
      {
        name: "Monto Total",
        data:
          paymentsData.length > 0
            ? paymentsData.map((item) => item.total_amount)
            : [0], // Muestra 0 si no hay datos
      },
    ],
  };

  const options: ApexOptions = {
    chart: {
      type: "bar",
    },
    xaxis: {
      categories:
        paymentsData.length > 0
          ? paymentsData.map((item) => item.status)
          : ["Sin Datos"], // Placeholder si no hay datos
    },
    plotOptions: {
      bar: {
        columnWidth: "50%",
      },
    },
    title: {
      text:
        paymentsData.length > 0
          ? "Ingresos por Estado de Pago"
          : "No hay datos disponibles",
      style: {
        fontSize: "20px",
        fontWeight: "bold",
      },
    },
    yaxis: {
      title: {
        text: "Monto",
      },
    },
  };

  return (
    <Box sx={{ padding: 2, minHeight: "350px" }}>
      <Typography variant="h4" gutterBottom>
        Pagos Mensuales
      </Typography>

      {/* Selector de Mes */}
      <TextField
        type="month"
        value={month}
        onChange={handleMonthChange}
        variant="outlined"
        fullWidth
        margin="normal"
      />

      {/* Indicador de carga */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", marginY: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Manejo de errores */}
      {error && (
        <Typography color="error" variant="body1">
          Error: {error}
        </Typography>
      )}

      {/* No mostrar la gráfica si no hay datos */}
      {!loading && paymentsData.length > 0 && (
        <Box sx={{ width: "100%", marginTop: 2 }}>
          <ApexCharts
            options={options}
            series={chartData.series}
            type="bar"
            height={350}
          />
        </Box>
      )}

      {/* Mensaje cuando no hay resultados */}
      {!loading && paymentsData.length === 0 && (
        <Typography variant="body1" sx={{ marginTop: 2 }}>
          No hay datos disponibles para los pagos del mes seleccionado.
        </Typography>
      )}
    </Box>
  );
};

export default MonthlyPayments;
